/**
 * accessGate, thin client for the backend access-gate endpoints.
 *
 * The password lives only in backend/.env. This module performs an
 * async POST to validate it server-side, and exposes a verifier that
 * checks an issued HMAC token. NEITHER the password nor the token
 * secret is present anywhere in the frontend source or bundle.
 */
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Validate a password against the backend. Resolves to an opaque token
 * string on success, or throws on failure. The token is what the caller
 * persists (sessionStorage etc.) for subsequent verify() calls.
 *
 * @param {string} password
 * @returns {Promise<{ token: string, expiresIn: number }>}
 */
export async function authenticateGate(password) {
  const { data } = await axios.post(`${API}/api/access-gate/auth`, { password });
  if (!data?.token) {
    throw new Error('Unexpected response from server.');
  }
  return { token: data.token, expiresIn: data.expires_in || 0 };
}

/**
 * Verify a previously-issued token. Resolves true if valid, false
 * otherwise. Never throws on 401, only on network errors.
 *
 * @param {string} token
 * @returns {Promise<boolean>}
 */
export async function verifyGateToken(token) {
  if (!token) return false;
  try {
    await axios.get(`${API}/api/access-gate/verify`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return true;
  } catch (err) {
    if (err?.response?.status === 401) return false;
    // Network/server errors: treat as not-valid so the user is re-prompted.
    return false;
  }
}
